import React from 'react'
import { Table, Tooltip, Switch } from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import useMentorDetail from 'src/Hooks/MentorHooks/useMentorDetail'
import { CommentOutlined } from '@ant-design/icons'
import { thumbUrl } from 'src/Common/fileAsset'
import { academicYearLabel } from 'src/Common/academicYear'
import 'src/Pages/LibraryPages/librarypages.scss'

// api-dev2: ideaType may be numeric (0-4); map to a label, but leave a string value untouched.
const IDEA_TYPE_LABELS = {
  0: 'Computer Science',
  1: 'Entrepreneurship',
  2: 'Engineering',
  3: 'Robotics',
  4: 'Design Thinking',
}
const ideaTypeLabel = (v) => (v === null || v === undefined ? v : IDEA_TYPE_LABELS[v] ?? v)

const IdeaIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 18h6M10 21h4" />
    <path d="M12 3a6 6 0 0 0-4 10.5c.7.8 1 1.5 1 2.5h6c0-1 .3-1.7 1-2.5A6 6 0 0 0 12 3z" />
  </svg>
)

const BackIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </svg>
)

const MentorDetail = () => {
  const { id } = useParams()
  const {
    data,
    loading,
    showApplication,
    setShowApplication,
    handleToggle,
    sharing,
    handleShareProjectPlan,
  } = useMentorDetail(id)
  const navigate = useNavigate()

  const handleGroupConversationClick = () => {
    if (data?.program?.conversationId) {
      navigate('/messaging', { state: { selectedChatId: data.program.conversationId } })
    }
  }

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Uploaded On',
      dataIndex: 'uploadedOn',
      key: 'uploadedOn',
      render: (text, record) =>
        record.submittedFileName ? new Date(text).toLocaleString() : 'N/A',
    },
    {
      title: 'Submitted File Name',
      dataIndex: 'submittedFileName',
      key: 'submittedFileName',
      render: (text, record) =>
        record.submittedFileName ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {thumbUrl(record.submittedFileThumbnailUrl) && (
              <img
                src={thumbUrl(record.submittedFileThumbnailUrl)}
                alt={text}
                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
              />
            )}
            <Tooltip title="Download will be available soon">
              <span style={{ color: '#16233f' }}>{text}</span>
            </Tooltip>
          </div>
        ) : (
          'No File Submitted Yet'
        ),
    },
    {
      title: 'Enable Resubmission',
      key: 'action',
      render: (_, record) => {
        const isDisabled = !record.submittedFileName

        return (
          <Tooltip title={isDisabled ? 'No file is submitted yet!' : ''}>
            <span>
              <Switch
                checked={record.enableSubmission}
                disabled={isDisabled}
                onChange={() => handleToggle(record.id)}
              />
            </span>
          </Tooltip>
        )
      },
    },
  ]

  return (
    <div className="lpg">
      <div className="lpg__head">
        <div className="lpg__head-main">
          <span className="lpg__head-ic">
            <IdeaIcon />
          </span>
          <div>
            <h1 className="lpg__title">Idea Detail</h1>
            <p className="lpg__sub">Idea details.</p>
          </div>
        </div>
        <button type="button" className="lpg__back" onClick={() => navigate(-1)}>
          <BackIcon /> Back
        </button>
      </div>

      {loading ? (
        <div className="lpg__state">
          <div className="lpg__spin" />
          Loading...
        </div>
      ) : data ? (
        showApplication ? (
          <div className="lpg__panel-box">
            <div className="lpg__toolbar">
              <h2 className="lpg__panel-name">Idea Factory Detail</h2>
              <button type="button" className="lpg__back" onClick={() => setShowApplication(false)}>
                <BackIcon /> Back
              </button>
            </div>

            <div className="lpg__form">
              <div className="lpg__field">
                <span className="lpg__label">Idea Type</span>
                <div>{ideaTypeLabel(data.application?.ideaType)}</div>
              </div>
              <div className="lpg__field">
                <span className="lpg__label">Describe the Idea</span>
                <div>{data.application?.description}</div>
              </div>
              <div className="lpg__field">
                <span className="lpg__label">Explain the Benefit (Max 200 words)</span>
                <div>{data.application?.benefitOfIdea}</div>
              </div>
              <div className="lpg__field">
                <span className="lpg__label">
                  What resources are needed to complete this project successfully?
                </span>
                <div>{data.application?.resourcesForProject}</div>
              </div>
              <div className="lpg__field">
                <span className="lpg__label">What are the potential challenges and risks?</span>
                <div>{data.application?.potentialRisks}</div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="lpg__panel-box" style={{ marginBottom: '18px' }}>
              <div className="lpg__toolbar">
                <button
                  type="button"
                  className="lpg__btn-primary"
                  onClick={() => setShowApplication(!showApplication)}
                >
                  Student Idea Factory Submitted Detail
                </button>
              </div>

              <div className="lpg__form">
                <div className="lpg__field">
                  <span className="lpg__label">Name</span>
                  <div>{data?.student?.studentName}</div>
                </div>
                <div className="lpg__field">
                  <span className="lpg__label">Year</span>
                  <div>{academicYearLabel(data?.student?.year) || data?.student?.year}</div>
                </div>
              </div>

              <div className="lpg__actions">
                <button
                  type="button"
                  className="lpg__btn-primary"
                  onClick={handleGroupConversationClick}
                >
                  <CommentOutlined /> Chat with Student
                </button>
                {data?.program?.isProjectPlanSent ? (
                  <span style={{ color: '#059669', fontWeight: 700, fontSize: '0.85rem' }}>
                    Project plan shared successfully.
                  </span>
                ) : (
                  <button
                    type="button"
                    className="lpg__btn-primary"
                    disabled={sharing}
                    onClick={handleShareProjectPlan}
                  >
                    {sharing ? 'Please wait...' : 'Share Project Plan'}
                  </button>
                )}
              </div>
            </div>

            <div className="lpg__panel-box">
              <Table
                columns={columns}
                dataSource={data.program?.uploadedTempletes || []}
                rowKey="id"
                bordered
              />
            </div>
          </>
        )
      ) : (
        <div className="lpg__state">No data available</div>
      )}
    </div>
  )
}

export default MentorDetail
